import { useEffect, useRef } from 'react';

import { getTimestampNow } from '@/utils/dateTime';
import { devError, wakeLockLog } from '@/utils/logging';

/**
 * Interface representing a Wake Lock object returned by the Wake Lock API.
 * @interface
 */
interface WakeLock {
  release: () => Promise<void>;
}

type WakeLockObject = {
  releaseWakeLock: () => Promise<void>;
  requestWakeLock: () => Promise<void>;
  isSupported: boolean;
};

/**
 * A React hook that manages the Wake Lock API to prevent the screen from turning off.
 *
 * This hook provides functionality to request and release a screen wake lock, which keeps
 * the screen active even when the device would normally dim or turn off the display.
 * It automatically handles various scenarios such as:
 * - Tab visibility changes
 * - Page focus events
 * - Component unmounting
 * - Browser support detection
 *
 * The wake lock is automatically:
 * - Requested when the page becomes visible
 * - Re-requested when the page regains focus
 * - Released when the component unmounts
 *
 * @example
 * ```tsx
 * const MyComponent = () => {
 *   const { isSupported, requestWakeLock, releaseWakeLock } = useWakeLock();
 *
 *   if (!isSupported) {
 *     return <div>Wake Lock is not supported in this browser</div>;
 *   }
 *
 *   return <div>Screen will stay awake</div>;
 * };
 * ```
 *
 * @returns {WakeLockObject} An object containing:
 *   - `requestWakeLock`: Function to manually request a wake lock
 *   - `releaseWakeLock`: Function to manually release the wake lock
 *   - `isSupported`: Boolean indicating if Wake Lock API is supported in the current browser
 */
export const useWakeLock = (): WakeLockObject => {
  const wakeLockRef = useRef<WakeLock | null>(null);
  const wakeLockPromiseRef = useRef<Promise<WakeLock> | null>(null);

  /**
   * Helper function to log messages with timestamps.
   * @param level - The log level ('log' or 'error')
   * @param message - The message to log
   * @param args - Additional arguments to pass to console[level]
   */
  const logWithTime = (
    level: 'log' | 'error',
    message: string,
    ...args: unknown[]
  ) => {
    if (process.env.NODE_ENV !== 'development') {
      return;
    }
    // eslint-disable-next-line no-console
    console[level](`[DEV][${getTimestampNow()}] WakeLock: ${message}`, ...args);
  };

  /**
   * Requests a wake lock from the system.
   * This function will:
   * - Check for browser support
   * - Verify the page is visible
   * - Request the wake lock
   * - Handle any errors that occur during the request
   * @returns {Promise<void>}
   */
  const requestWakeLock = async () => {
    try {
      if (!('wakeLock' in navigator)) {
        wakeLockLog('Wake Lock API not supported in this browser');
        return;
      }

      if (document.visibilityState !== 'visible') {
        wakeLockLog('Page not visible, skipping WakeLock request');
        return;
      }

      wakeLockLog('Requesting WakeLock...');
      wakeLockPromiseRef.current = navigator.wakeLock.request('screen');
      wakeLockRef.current = await wakeLockPromiseRef.current;
      wakeLockLog('WakeLock acquired successfully');
    } catch (err) {
      if (
        err instanceof Error &&
        err.name === 'NotAllowedError' &&
        err.message.includes('not visible')
      ) {
        wakeLockLog('WakeLock not allowed: page is not visible');
      } else {
        wakeLockLog('Error requesting WakeLock:', err);
      }
    }
  };

  /**
   * Releases the currently held wake lock if one exists.
   * This function will:
   * - Check if there's an active wake lock
   * - Release it if it exists
   * - Handle any errors that occur during release
   * @returns {Promise<void>}
   */
  const releaseWakeLock = async () => {
    if (wakeLockRef.current) {
      try {
        await wakeLockRef.current.release();
        wakeLockRef.current = null;
        wakeLockLog('WakeLock released successfully');
      } catch (err) {
        devError('WakeLock: Error releasing WakeLock:', err);
      }
    }
  };

  useEffect(() => {
    if (document.visibilityState === 'visible') {
      const timer = setTimeout(() => {
        requestWakeLock();
      }, 1000);

      return () => clearTimeout(timer);
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        logWithTime('log', 'Tab became visible, requesting WakeLock...');
        setTimeout(() => requestWakeLock(), 500);
      } else {
        logWithTime('log', 'Tab is now hidden');
        // Uncomment the next line if you want to release the wake lock when the tab is hidden
        // releaseWakeLock();
      }
    };

    const handleFocus = () => {
      logWithTime('log', 'Page received focus');
      if (!wakeLockRef.current) {
        logWithTime('log', 'WakeLock is not active, requesting it...');
        setTimeout(() => requestWakeLock(), 500);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      logWithTime('log', 'Component unmounting, cleaning up WakeLock...');
      releaseWakeLock();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  return {
    requestWakeLock,
    releaseWakeLock,
    isSupported: 'wakeLock' in navigator,
  };
};
